import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import axiosPixLink from 'config/axiosPixLink';
import NotificationIcon from 'components/shared/NotificationIcon';
import NotificationSummaryCards from 'components/shared/NotificationSummaryCards';
import NotificationFilters from 'components/shared/NotificationFilters';
import NotificationActionCard from 'components/shared/NotificationActionCard';
import { useNotificationFilters } from 'utils/hooks/useNotificationFilters';

import './Notifications.css';

// L'utilisateur est déduit du jeton : aucune donnée de rôle à envoyer.
const API = {
  getCount: '/notification/count',
  getActions: '/notification/actions',
  getInformations: '/notification/informations',
  markRead: (id) => `/notification/${id}/read`,
  treat: (id) => `/notification/${id}/treated`,
  untreat: (id) => `/notification/${id}/untreated`,
  readAll: '/notification/read-all',
};

export default function Notifications() {
  const { t } = useTranslation();
  const history = useHistory();
  const { filters, setFilters, resetFilters, filtersActive } = useNotificationFilters();

  const [counts, setCounts] = useState(null);
  const [isCountsLoading, setIsCountsLoading] = useState(true);

  const [actions, setActions] = useState([]);
  const [isActionsLoading, setIsActionsLoading] = useState(true);
  const [isActionsError, setIsActionsError] = useState(false);

  const getCounts = () => {
    axiosPixLink
      .get(API.getCount)
      .then((res) => setCounts(res.data))
      .catch((e) => {
        console.error('GET /notification/count', e?.response?.status, e?.response?.data || e.message);
        setCounts(null);
      })
      .finally(() => setIsCountsLoading(false));
  };

  useEffect(() => {
    getCounts();
  }, []);

  // Liste des actions : rechargée à chaque changement d'onglet, d'étape ou de recherche.
  // Le tri, les filtres et la pagination sont faits par le back.
  useEffect(() => {
    let isCancelled = false; // ignore une réponse arrivée après un nouveau filtre
    setIsActionsLoading(true);
    setIsActionsError(false);

    axiosPixLink
      .get(API.getActions, {
        params: {
          tab: filters.tab,
          step: filters.step !== 'all' ? filters.step : undefined,
          q: filters.q || undefined,
          page: 0,
          size: filters.size,
        },
      })
      .then((res) => {
        if (!isCancelled) setActions(res.data?.items || []);
      })
      .catch((e) => {
        console.error('GET /notification/actions', e?.response?.status, e?.response?.data || e.message);
        if (!isCancelled) setIsActionsError(true);
      })
      .finally(() => {
        if (!isCancelled) setIsActionsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [filters.tab, filters.step, filters.q, filters.size]);

  const renderActions = () => {
    if (isActionsLoading) {
      return (
        <div className="notif-list">
          <div className="notif-skeleton" />
          <div className="notif-skeleton" />
          <div className="notif-skeleton" />
        </div>
      );
    }
    if (isActionsError) {
      return (
        <div className="notif-error" role="alert">
          <strong>{t('Le chargement des notifications a échoué.')}</strong>
          <span>{t('Vérifie ta connexion puis réessaie.')}</span>
        </div>
      );
    }
    if (!actions.length) {
      return (
        <div className="notif-empty">
          <span className="notif-empty__icon">
            <NotificationIcon name="check" size={26} />
          </span>
          <strong>{filters.tab === 'done' ? t('Aucune action traitée') : t('Aucune action en attente')}</strong>
          <p>
            {filters.tab === 'done'
              ? t('Les actions réalisées apparaîtront ici.')
              : t('Rien à traiter pour ces filtres.')}
          </p>
          {filtersActive && (
            <button type="button" className="notif-btn-secondary" onClick={resetFilters}>
              {t('Réinitialiser les filtres')}
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="notif-list">
        {actions.map((n) => (
          <NotificationActionCard key={n.id} notification={n} />
        ))}
      </div>
    );
  };

  return (
    <main className="notif-page">
      <div className="notif-title">
        <button type="button" className="notif-back" aria-label={t('Retour')} onClick={() => history.goBack()}>
          <NotificationIcon name="back" size={22} strokeWidth={2.2} />
        </button>
        <h1>{t('Notifications')}</h1>
      </div>

      <NotificationSummaryCards
        counts={counts}
        loading={isCountsLoading}
        filters={filters}
        onSelectActionTab={(tab) => setFilters({ tab, focus: 'actions' })}
        onSelectInfoTab={(info) => setFilters({ info, focus: 'infos' })}
      />

      <NotificationFilters filters={filters} setFilters={setFilters} />

      <div className="notif-columns">
        <section className="notif-panel notif-panel--actions" aria-labelledby="notif-actions-title">
          <div className="notif-panel__head">
            <span className="notif-panel__icon">
              <NotificationIcon name="todo" size={22} />
            </span>
            <div>
              <h2 id="notif-actions-title">{t('Actions à faire')}</h2>
              <p>{t('Emails qui attendent une intervention de ta part dans Pixlink.')}</p>
            </div>
          </div>
          {/* À venir : onglets À faire / Urgentes / Traitées + pagination */}
          {renderActions()}
        </section>

        <aside className="notif-panel notif-panel--infos" aria-labelledby="notif-infos-title">
          <div className="notif-panel__head">
            <span className="notif-panel__icon">
              <NotificationIcon name="info" size={22} />
            </span>
            <div>
              <h2 id="notif-infos-title">{t('Informations')}</h2>
              <p>{t('Emails informatifs : rien à faire, juste pour suivre.')}</p>
            </div>
          </div>
          {/* Étape suivante : onglets + liste des informations + pagination */}
        </aside>
      </div>
    </main>
  );
}
